import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronRight,
  CheckCircle,
  XCircle,
  HelpCircle,
  Clock,
  User,
  Copy,
  Eye,
  Users,
  StickyNote,
  Pin,
  Trash2,
  Plus,
  X,
  Pencil,
  Save,
  RotateCcw,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useAuth } from '@core/auth/useAuth';
import { clientService } from '@core/supabase/services/clientService';
import { jobService } from '@core/supabase/services/jobService';
import { applicationService } from '@core/supabase/services/applicationService';
import { emailService } from '@core/supabase/services/emailService';
import { clientContactService, ClientContact } from '@core/supabase/services/clientContactService';
import { clientNoteService, ClientNote } from '@core/supabase/services/clientNoteService';
import { Client, Job, Application, JobStatus, EmailThread, CTAAction } from '@core/types';
import { RecordHeader } from '@shared/components/ui/RecordHeader';
import { RecordDetailLayout } from '@shared/components/ui/RecordDetailLayout';
import { RecordMetaCard } from '@shared/components/ui/RecordMetaCard';
import { RightRailWithAssistant } from '@shared/components/ui/RightRailWithAssistant';
import { useTrackRecentlyViewed } from '@shared/hooks/useRecentlyViewed';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { detailTbBtn, detailTbNeutral, detailTbDanger } from '@shared/lib/detailToolbarStyles';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';
import { StatusBadge } from '@shared/components/ui/StatusBadge';
import { DataTableToolbar } from '@shared/components/ui/DataTableToolbar';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { ClientForm } from './ClientForm';
import { ClientOnboardingPanel } from './ClientOnboardingPanel';
import { ClientProfileSummary } from './ClientProfileSummary';
import { CandidateCollapsiblePanel } from '@modules/candidates/CandidateCollapsiblePanel';
import { formatDateTimeGmt, formatDateOrDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { CandidateApplicationDetailModal } from '@modules/candidates/CandidateApplicationDetailModal';
import { isClientReadyForRecruitment } from '@core/clients/clientEngagementGate';
import { rechartsGridStroke, rechartsTooltipStyle } from '@shared/chart/rechartsTheme';

type TabId = 'overview' | 'applications' | 'jobs' | 'emails' | 'onboarding' | 'parameters';

function recordInitials(name: string) {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return '?';
  if (p.length === 1) return p[0]!.slice(0, 2).toUpperCase();
  return (p[0]![0]! + p[p.length - 1]![0]!).toUpperCase();
}

const CTA_LABEL: Record<string, string> = { accept: 'Shortlisted', reject: 'Passed', query: 'Query' };
const CTA_ICON: Record<CTAAction, React.ReactNode> = {
  accept: <CheckCircle className="w-3.5 h-3.5 text-green-400" />,
  reject: <XCircle className="w-3.5 h-3.5 text-red-400" />,
  query: <HelpCircle className="w-3.5 h-3.5 text-yellow-400" />,
};

function RecipientInfoPopover({
  thread,
  clientName,
  onClose,
  anchor,
}: {
  thread: EmailThread;
  clientName?: string;
  onClose: () => void;
  anchor: { top: number; left: number };
}) {
  const copyEmail = () => {
    navigator.clipboard.writeText(thread.recipient_email);
  };
  return (
    <>
      <div className="fixed inset-0 z-10" aria-hidden onClick={onClose} />
      <div
        className="fixed z-20 w-72 rounded-lg border vscode-border vscode-bg-elevated shadow-xl p-4"
        style={{ top: anchor.top, left: anchor.left }}
      >
        <div className="flex items-start justify-between gap-2 mb-3">
          <User className="w-5 h-5 vscode-text-secondary flex-shrink-0 mt-0.5" />
          <span className="text-xs font-semibold vscode-text-secondary uppercase">Recipient</span>
        </div>
        <p className="font-medium vscode-text-primary">{thread.recipient_name || '—'}</p>
        <p className="text-sm vscode-text-secondary mt-0.5 break-all">{thread.recipient_email}</p>
        {clientName && <p className="text-xs vscode-text-tertiary mt-1">Client: {clientName}</p>}
        <button
          type="button"
          onClick={copyEmail}
          className="mt-3 flex items-center gap-1.5 text-xs text-brand-400 hover:text-brand-300"
        >
          <Copy className="w-3.5 h-3.5" /> Copy email
        </button>
      </div>
    </>
  );
}

const JOB_STATUS_BADGE: Record<JobStatus, string> = {
  open: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  on_hold: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  filled: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  closed: 'bg-[var(--vscode-bg-tertiary)] vscode-text-secondary',
};

const VALID_TABS: TabId[] = ['overview', 'applications', 'jobs', 'emails', 'onboarding', 'parameters'];

export function ClientDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [client, setClient] = useState<Client | null>(null);

  useTrackRecentlyViewed(
    useMemo(
      () =>
        client
          ? {
              kind: 'client' as const,
              id: client.id,
              label: client.name || 'Client',
              sublabel: client.contact_name || client.contact_email || undefined,
              to: `/clients/${client.id}`,
            }
          : null,
      [client?.id, client?.name, client?.contact_name, client?.contact_email],
    ),
  );
  const [jobs, setJobs] = useState<Job[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDelete, setShowDelete] = useState(false);
  const [formKey, setFormKey] = useState(0);
  const tabFromUrl = searchParams.get('tab') as TabId | null;
  const [activeTab, setActiveTab] = useState<TabId>(VALID_TABS.includes(tabFromUrl ?? '') ? tabFromUrl! : 'overview');

  // Sync tab with URL
  useEffect(() => {
    const tab = searchParams.get('tab') as TabId | null;
    if (tab && VALID_TABS.includes(tab)) setActiveTab(tab);
  }, [searchParams]);

  const setTab = (tab: TabId) => {
    setActiveTab(tab);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (tab === 'overview') {
        next.delete('tab');
      } else {
        next.set('tab', tab);
        next.delete('edit');
      }
      return next;
    }, { replace: true });
  };

  const editQuery = searchParams.get('edit');
  useEffect(() => {
    if (activeTab !== 'overview' && editQuery === '1') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      }, { replace: true });
    }
  }, [activeTab, editQuery, setSearchParams]);
  const [selectedJobIds, setSelectedJobIds] = useState<Set<string>>(new Set());
  const [selectedAppIds, setSelectedAppIds] = useState<Set<string>>(new Set());
  const [selectedEmailIds, setSelectedEmailIds] = useState<Set<string>>(new Set());
  const [jobPage, setJobPage] = useState(1);
  const [jobPageSize, setJobPageSize] = useState(5);
  const [jobTotal, setJobTotal] = useState(0);
  const [appPage, setAppPage] = useState(1);
  const [appPageSize, setAppPageSize] = useState(5);
  const [appTotal, setAppTotal] = useState(0);
  const [emailThreads, setEmailThreads] = useState<EmailThread[]>([]);
  const [emailPage, setEmailPage] = useState(1);
  const [emailPageSize, setEmailPageSize] = useState(5);
  const [allApplications, setAllApplications] = useState<Application[]>([]);
  const [paramsEditing, setParamsEditing] = useState(false);
  const [paramsForm, setParamsForm] = useState({ name: '', description: '', domain: '', contact_name: '', contact_email: '', contact_phone: '', industry: '' });
  const [recipientPopover, setRecipientPopover] = useState<{ thread: EmailThread; anchor: { top: number; left: number } } | null>(null);
  const [appModal, setAppModal] = useState<Application | null>(null);
  const jobCheckRef = useRef<HTMLInputElement>(null);
  const appCheckRef = useRef<HTMLInputElement>(null);
  const emailCheckRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  // CLI-010: contacts state
  const [contacts, setContacts] = useState<ClientContact[]>([]);
  const [contactForm, setContactForm] = useState({ full_name: '', title: '', email: '', phone: '', is_primary: false, notes: '' });
  const [contactFormOpen, setContactFormOpen] = useState(false);
  const [savingContact, setSavingContact] = useState(false);

  // CLI-030: notes state
  const [clientNotes, setClientNotes] = useState<ClientNote[]>([]);
  const [noteInput, setNoteInput] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  useEffect(() => {
    if (!id) return;
    const storageKey = 'ggj.b2b.clientDetail.id';
    const lastId = sessionStorage.getItem(storageKey);
    if (lastId !== id) {
      sessionStorage.setItem(storageKey, id);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      }, { replace: true });
    }
    clientService.getById(id).then(setClient).finally(() => setLoading(false));
  }, [id, setSearchParams]);

  useEffect(() => {
    if (!user?.orgId || !id) return;
    jobService
      .list(user.orgId, { clientId: id, page: jobPage, pageSize: jobPageSize })
      .then((result) => {
        const r = result as { data: Job[]; total: number };
        setJobs(r.data);
        setJobTotal(r.total);
      });
  }, [user?.orgId, id, jobPage, jobPageSize]);

  useEffect(() => {
    if (!user?.orgId || !id) return;
    applicationService.list(user.orgId, { clientId: id }).then((list) => {
      const apps = Array.isArray(list) ? list : [];
      setAppTotal(apps.length);
      const start = (appPage - 1) * appPageSize;
      setApplications(apps.slice(start, start + appPageSize));
    });
  }, [user?.orgId, id, appPage, appPageSize]);

  useEffect(() => {
    if (!user?.orgId || !id) return;
    emailService.listByClient(user.orgId, id).then(setEmailThreads);
  }, [user?.orgId, id]);

  useEffect(() => {
    if (!id) return;
    clientContactService.listByClient(id).then(setContacts);
    clientNoteService.listByClient(id).then(setClientNotes);
  }, [id]);

  useEffect(() => {
    if (!user?.orgId || !id) return;
    applicationService.list(user.orgId, { clientId: id }).then((list) =>
      setAllApplications(Array.isArray(list) ? list : [])
    );
  }, [user?.orgId, id]);

  useEffect(() => {
    if (client) {
      setParamsForm({
        name: client.name ?? '',
        description: client.notes ?? '',
        domain: client.domain ?? '',
        contact_name: client.contact_name ?? '',
        contact_email: client.contact_email ?? '',
        contact_phone: client.contact_phone ?? '',
        industry: client.industry ?? '',
      });
    }
  }, [client?.id, client]);

  useEffect(() => {
    if (jobCheckRef.current) {
      jobCheckRef.current.indeterminate =
        jobs.length > 0 && selectedJobIds.size > 0 && selectedJobIds.size < jobs.length;
    }
  }, [jobs.length, selectedJobIds.size]);

  useEffect(() => {
    if (appCheckRef.current) {
      appCheckRef.current.indeterminate =
        applications.length > 0 && selectedAppIds.size > 0 && selectedAppIds.size < applications.length;
    }
  }, [applications.length, selectedAppIds.size]);

  useEffect(() => {
    if (emailCheckRef.current) {
      const unique = [...new Map(emailThreads.map((t) => [t.id, t])).values()];
      const start = (emailPage - 1) * emailPageSize;
      const paginated = unique.slice(start, start + emailPageSize);
      emailCheckRef.current.indeterminate =
        paginated.length > 0 && selectedEmailIds.size > 0 && selectedEmailIds.size < paginated.length;
    }
  }, [emailThreads.length, selectedEmailIds.size, emailPage, emailPageSize]);

  const emailThreadsFiltered = useMemo(() => {
    const unique = [...new Map(emailThreads.map((t) => [t.id, t])).values()];
    const start = (emailPage - 1) * emailPageSize;
    return unique.slice(start, start + emailPageSize);
  }, [emailThreads, emailPage, emailPageSize]);
  const emailTotal = useMemo(
    () => [...new Map(emailThreads.map((t) => [t.id, t])).values()].length,
    [emailThreads]
  );

  const handleDelete = async () => {
    if (!id) return;
    await clientService.delete(id);
    setShowDelete(false);
    navigate('/clients');
  };

  const handleParamsSave = async () => {
    if (!client || !id) return;
    const updated = await clientService.update(id, {
      name: paramsForm.name.trim(),
      notes: paramsForm.description || null,
      domain: paramsForm.domain || null,
      contact_name: paramsForm.contact_name || null,
      contact_email: paramsForm.contact_email || null,
      contact_phone: paramsForm.contact_phone || null,
      industry: paramsForm.industry || null,
    });
    setClient(updated);
    setParamsEditing(false);
    setParamsForm({
      name: updated.name ?? '',
      description: updated.notes ?? '',
      domain: updated.domain ?? '',
      contact_name: updated.contact_name ?? '',
      contact_email: updated.contact_email ?? '',
      contact_phone: updated.contact_phone ?? '',
      industry: updated.industry ?? '',
    });
  };

  const handleParamsCancel = () => {
    setParamsEditing(false);
    if (client) {
      setParamsForm({
        name: client.name ?? '',
        description: client.notes ?? '',
        domain: client.domain ?? '',
        contact_name: client.contact_name ?? '',
        contact_email: client.contact_email ?? '',
        contact_phone: client.contact_phone ?? '',
        industry: client.industry ?? '',
      });
    }
  };

  // Hiring heartbeat: applications submitted per week (last 8 weeks)
  const heartbeatData = (() => {
    const now = new Date();
    const weeks: { week: string; count: number }[] = [];
    for (let i = 7; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i * 7);
      const weekStart = new Date(d);
      weekStart.setHours(0, 0, 0, 0);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekEnd.getDate() + 7);
      const count = allApplications.filter((a) => {
        const t = new Date(a.submitted_at).getTime();
        return t >= weekStart.getTime() && t < weekEnd.getTime();
      }).length;
      weeks.push({ week: formatDateTimeGmt(d.toISOString()), count });
    }
    return weeks;
  })();

  if (loading && !client) return <FullPageSpinner />;
  if (!client) return <div className="vscode-text-secondary">Client not found.</div>;
  if (!user?.orgId) return null;

  const isEditing = activeTab === 'overview' && searchParams.get('edit') === '1';

  const handleSaveContact = async () => {
    if (!user?.orgId || !id || !contactForm.full_name.trim()) return;
    setSavingContact(true);
    try {
      const created = await clientContactService.create({
        org_id: user.orgId,
        client_id: id,
        full_name: contactForm.full_name.trim(),
        title: contactForm.title || null,
        email: contactForm.email || null,
        phone: contactForm.phone || null,
        is_primary: contactForm.is_primary,
        notes: contactForm.notes || null,
        created_by: user.name,
      });
      setContacts((prev) => [...prev, created].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0)));
      setContactForm({ full_name: '', title: '', email: '', phone: '', is_primary: false, notes: '' });
      setContactFormOpen(false);
    } finally {
      setSavingContact(false);
    }
  };

  const handleAddNote = async () => {
    if (!user?.orgId || !id || !noteInput.trim()) return;
    setSavingNote(true);
    try {
      const created = await clientNoteService.create(user.orgId, id, noteInput.trim(), user.name);
      setClientNotes((prev) => [created, ...prev]);
      setNoteInput('');
    } finally {
      setSavingNote(false);
    }
  };

  const tabs: { id: TabId; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'applications', label: appTotal > 0 ? `Applications (${appTotal})` : 'Applications' },
    { id: 'jobs', label: jobTotal > 0 ? `Jobs (${jobTotal})` : 'Jobs' },
    { id: 'emails', label: emailTotal > 0 ? `Emails (${emailTotal})` : 'Emails' },
    { id: 'onboarding', label: 'Onboarding' },
    { id: 'parameters', label: 'Parameters' },
  ];


  return (
    <div className="w-full min-w-0 mx-auto max-w-none">
      <RecordDetailLayout
        header={
          <RecordHeader
            avatarInitials={recordInitials(client.name)}
            title={client.name}
            meta={[client.industry, client.domain].filter(Boolean).join(' · ') || 'Hiring company client'}
            breadcrumb={[
              { label: 'Organisation', href: '/clients' },
              { label: 'Clients', href: '/clients' },
              { label: client.name },
            ]}
            actionsEnd={
              <ListOperationsDropdown
                objectHistoryHref={objectHistoryUrl('clients')}
                onExport={(fmt, scope) => listExportNotImplemented('Client', fmt, scope)}
              />
            }
          />
        }
        metaRail={
          <RightRailWithAssistant
            recordId={client.id}
            entityKind="client"
            details={
              <RecordMetaCard
                title="Record"
                rows={[
                  { label: 'Created', value: formatDateTimeGmt(client.created_at) },
                  { label: 'Updated', value: '—' },
                  { label: 'Industry', value: client.industry?.trim() || '—' },
                  { label: 'Open jobs', value: String(jobTotal) },
                  { label: 'Applications', value: String(appTotal) },
                ]}
                footer={
                  <Link to={objectHistoryUrl('clients')} className="text-sm text-[var(--vscode-text-link)] hover:underline">
                    View history
                  </Link>
                }
              />
            }
          />
        }
        main={
          <>
      {/* Onboarding alert — only shown if incomplete */}
      {!isClientReadyForRecruitment(client) && (
        <div className="mb-3 text-xs text-amber-700 dark:text-amber-200/90 rounded-md border border-amber-400/40 bg-amber-50 dark:bg-amber-500/5 px-3 py-2">
          Onboarding in progress — see the <button type="button" className="underline" onClick={() => setTab('onboarding')}>Onboarding tab</button> to complete compliance before opening jobs.
        </div>
      )}

      {/* Tab nav */}
      <nav className="ggj-cand-tablist mb-4" aria-label="Client sections">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            onClick={() => setTab(tab.id)}
            className={`ggj-cand-tab ${activeTab === tab.id ? 'ggj-cand-tab--active' : ''}`}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {/* ── Overview tab ────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(340px,30%)] gap-3 lg:gap-5 items-start">

          {/* Left: profile card with toolbar + summary + form */}
          <div className="ggj-param-group overflow-hidden order-1">
            {/* Inline toolbar */}
            <div className="ggj-profile-toolbar">
              <div className="flex flex-wrap items-center gap-2">
                {isEditing ? (
                  <>
                    <button type="submit" form="client-form" className={`${detailTbBtn} ${detailTbNeutral}`}>
                      <Save className="gg-toolbar-ic gg-toolbar-ic--violet shrink-0" aria-hidden />
                      <span>[Save]</span>
                    </button>
                    <button
                      type="button"
                      className={`${detailTbBtn} ${detailTbDanger}`}
                      onClick={() => {
                        setSearchParams((prev) => {
                          const next = new URLSearchParams(prev);
                          next.delete('edit');
                          return next;
                        }, { replace: true });
                        setFormKey((k) => k + 1);
                      }}
                    >
                      <RotateCcw className="gg-toolbar-ic gg-toolbar-ic--red shrink-0" aria-hidden />
                      <span>[Discard]</span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={`${detailTbBtn} hover:bg-slate-200/90 dark:hover:bg-gray-700`}
                    onClick={() => {
                      setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.set('edit', '1');
                        return next;
                      }, { replace: true });
                    }}
                  >
                    <Pencil className="h-4 w-4 shrink-0 text-[var(--vscode-primary)]" aria-hidden />
                    <span className="text-slate-700 dark:text-gray-300">Edit</span>
                  </button>
                )}
              </div>
            </div>

            {/* At a glance */}
            <ClientProfileSummary
              client={client}
              jobCount={jobTotal}
              appCount={appTotal}
            />

            {/* Client form sections */}
            <ClientForm
              key={formKey}
              mode="edit"
              client={client}
              readOnly={!isEditing}
              orgId={user.orgId}
              onSave={(updated) => {
                setClient(updated);
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('edit');
                  return next;
                }, { replace: true });
              }}
              onDelete={() => setShowDelete(true)}
            />
          </div>

          {/* Right rail */}
          <aside className="ggj-rail-aside space-y-2 order-2 lg:sticky lg:top-4 self-start">
            {/* Hiring heartbeat */}
            <CandidateCollapsiblePanel storageKey={`${client.id}-heartbeat`} title="Hiring heartbeat">
              {heartbeatData.every((d) => d.count === 0) ? (
                <p className="text-xs vscode-text-tertiary text-center py-4">No activity in last 8 weeks</p>
              ) : (
                <ResponsiveContainer width="100%" height={150}>
                  <LineChart data={heartbeatData} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={rechartsGridStroke} />
                    <XAxis dataKey="week" tick={{ fontSize: 9 }} stroke="var(--vscode-text-tertiary)" />
                    <YAxis tick={{ fontSize: 9 }} stroke="var(--vscode-text-tertiary)" />
                    <Tooltip contentStyle={rechartsTooltipStyle} labelStyle={{ color: 'var(--vscode-text-tertiary)' }} />
                    <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2} dot={{ fill: '#10b981' }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CandidateCollapsiblePanel>

            {/* Primary contact */}
            {(client.contact_name || client.contact_email || client.contact_phone) && (
              <CandidateCollapsiblePanel storageKey={`${client.id}-primary-contact`} title="Primary contact">
                {client.contact_name && (
                  <p className="text-sm font-medium vscode-text-primary">{client.contact_name}</p>
                )}
                {client.contact_email && (
                  <a href={`mailto:${client.contact_email}`} className="text-xs text-[var(--vscode-primary)] hover:underline block mt-0.5">
                    {client.contact_email}
                  </a>
                )}
                {client.contact_phone && (
                  <p className="text-xs vscode-text-secondary mt-0.5">{client.contact_phone}</p>
                )}
              </CandidateCollapsiblePanel>
            )}

            {/* Key contacts list */}
            {contacts.length > 0 && (
              <CandidateCollapsiblePanel storageKey={`${client.id}-contacts-rail`} title={`Contacts (${contacts.length})`}>
                <div className="space-y-2">
                  {contacts.slice(0, 4).map((c) => (
                    <div key={c.id} className="text-xs">
                      <span className="font-medium vscode-text-primary">{c.full_name}</span>
                      {c.title && <span className="vscode-text-tertiary"> · {c.title}</span>}
                      {c.is_primary && <span className="ml-1 px-1 py-0.5 rounded bg-[var(--brand-accent-bg)] text-[var(--vscode-primary)] text-[10px]">primary</span>}
                      {c.email && <p className="vscode-text-secondary mt-0.5 truncate">{c.email}</p>}
                    </div>
                  ))}
                  {contacts.length > 4 && (
                    <button type="button" className="text-xs text-[var(--vscode-primary)] underline" onClick={() => setTab('contacts')}>
                      +{contacts.length - 4} more → Contacts tab
                    </button>
                  )}
                </div>
              </CandidateCollapsiblePanel>
            )}

            {/* Agreement summary */}
            {(client.commercial_agreement_label || client.contract_start_date) && (
              <CandidateCollapsiblePanel storageKey={`${client.id}-agreement`} title="Agreement">
                {client.commercial_agreement_label && (
                  <p className="text-sm vscode-text-primary">{client.commercial_agreement_label}</p>
                )}
                {client.mou_reference && (
                  <p className="text-xs vscode-text-secondary mt-0.5">MOU: {client.mou_reference}</p>
                )}
                {client.contract_start_date && (
                  <p className="text-xs vscode-text-secondary mt-0.5">
                    {client.contract_start_date}{client.contract_end_date ? ` → ${client.contract_end_date}` : ''}
                  </p>
                )}
              </CandidateCollapsiblePanel>
            )}
          </aside>
        </div>
      )}

      {/* ── Jobs tab — mirrors Applications tab layout ─────────── */}
      {activeTab === 'jobs' && (() => {
        // Build per-job application map from allApplications
        const appsByJob = allApplications.reduce<Record<string, Application[]>>((acc, app) => {
          if (!app.job_id) return acc;
          (acc[app.job_id] ??= []).push(app);
          return acc;
        }, {});

        const openJobs   = jobs.filter((j) => j.status === 'open').length;
        const onHoldJobs = jobs.filter((j) => j.status === 'on_hold').length;
        const filledJobs = jobs.filter((j) => j.status === 'filled').length;
        const closedJobs = jobs.filter((j) => j.status === 'closed').length;
        const totalAppsAcrossJobs = allApplications.length;
        const activeApps = allApplications.filter((a) => !['placed','rejected','closed','withdrawn'].includes(a.status)).length;

        const DT = 'vscode-text-secondary pr-2 min-w-0 text-sm';
        const DD = 'vscode-text-primary min-w-0 break-words text-sm';

        return (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(300px,28%)] gap-3 lg:gap-5 items-start">

              {/* ── Left: summary panel ── */}
              <div className="ggj-param-group overflow-hidden">
                {/* Toolbar */}
                <div className="ggj-profile-toolbar">
                  <span className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider flex-1">
                    Jobs ({jobTotal}) <span className="text-[var(--vscode-primary)] text-[10px] ml-2">●</span>
                  </span>
                  <Link to={`/jobs/new?clientId=${client.id}`} className="ggj-toolbar-btn">[+ New job]</Link>
                </div>

                {jobs.length === 0 ? (
                  <div className="ggj-profile-section-body py-10 text-center">
                    <p className="text-sm vscode-text-secondary font-medium">No jobs yet for this client.</p>
                    <Link to={`/jobs/new?clientId=${client.id}`} className="text-xs text-[var(--vscode-primary)] underline mt-2 inline-block">[Add first job]</Link>
                  </div>
                ) : (
                  <>
                    {/* ── Jobs summary ── */}
                    <div className="ggj-profile-section-h">Jobs summary</div>
                    <div className="ggj-profile-section-body">
                      <dl className="ggj-attr-grid">
                        <dt className={DT}>Total positions</dt><dd className={DD}>{jobTotal}</dd>
                        <dt className={DT}>Open</dt><dd className={`${DD} text-green-600 dark:text-green-400 font-semibold`}>{openJobs}</dd>
                        <dt className={DT}>On hold</dt><dd className={`${DD} text-amber-600 dark:text-amber-400`}>{onHoldJobs}</dd>
                        <dt className={DT}>Filled</dt><dd className={`${DD} text-blue-600 dark:text-blue-400`}>{filledJobs}</dd>
                        <dt className={DT}>Closed</dt><dd className={DD}>{closedJobs}</dd>
                        <dt className={DT}>Total applications</dt><dd className={DD}>{totalAppsAcrossJobs}</dd>
                        <dt className={DT}>Active in pipeline</dt><dd className={DD}>{activeApps}</dd>
                      </dl>
                    </div>

                    {/* ── Applied job — single attribute, multi-value list (all jobs × all candidates) ── */}
                    <div className="ggj-profile-section-h">Applied job</div>
                    <div className="ggj-profile-section-body">
                      <dl className="ggj-attr-grid">
                        <dt className={`${DT} lg:col-span-1`}>Applied job</dt>
                        <dd className={`${DD} lg:col-span-3 space-y-1.5`}>
                          {allApplications.length === 0 ? (
                            <span className="vscode-text-tertiary text-xs italic">No applications yet</span>
                          ) : (
                            [...allApplications]
                              .sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())
                              .map((app) => (
                                <button
                                  key={app.id}
                                  type="button"
                                  onClick={() => setAppModal(app)}
                                  className="flex items-baseline gap-1.5 text-left text-sm text-[var(--vscode-primary)] hover:underline w-full"
                                >
                                  <span className="font-semibold truncate shrink-0">{app.candidate?.full_name ?? '—'}</span>
                                  <span className="vscode-text-tertiary text-xs shrink-0">›</span>
                                  <span className="vscode-text-secondary text-xs truncate">{app.job?.title ?? '—'}</span>
                                  {app.candidate?.current_title && (
                                    <>
                                      <span className="vscode-text-tertiary text-xs shrink-0">·</span>
                                      <span className="vscode-text-tertiary text-xs truncate">{app.candidate.current_title}</span>
                                    </>
                                  )}
                                  <span className="vscode-text-tertiary text-[10px] shrink-0 ml-auto tabular-nums">
                                    {formatDateTimeGmt(app.submitted_at)}
                                  </span>
                                </button>
                              ))
                          )}
                        </dd>
                      </dl>
                    </div>
                  </>
                )}
              </div>

              {/* ── Right rail: per-job collapsible cards ── */}
              <aside className="ggj-rail-aside space-y-2 lg:sticky lg:top-4 self-start">
                {jobs.map((job) => {
                  const jobApps = appsByJob[job.id] ?? [];
                  const activeCount = jobApps.filter((a) => !['placed','rejected','closed','withdrawn'].includes(a.status)).length;
                  const staleCount = jobApps.filter((a) => {
                    const d = Math.floor((Date.now() - new Date(a.status_updated_at).getTime()) / 86400000);
                    return !['placed','rejected','closed','withdrawn'].includes(a.status) && d >= 5;
                  }).length;
                  return (
                    <CandidateCollapsiblePanel
                      key={job.id}
                      storageKey={`${client.id}-job-card-${job.id}`}
                      title={job.title.toUpperCase()}
                    >
                      <dl className="space-y-1 text-xs">
                        <div className="flex justify-between gap-2 items-center">
                          <dt className="vscode-text-tertiary">Status</dt>
                          <dd>
                            <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium ${JOB_STATUS_BADGE[job.status] ?? ''}`}>
                              {job.status.replace('_', ' ')}
                            </span>
                          </dd>
                        </div>
                        {job.location && (
                          <div className="flex justify-between gap-2">
                            <dt className="vscode-text-tertiary">Location</dt>
                            <dd className="vscode-text-primary">{job.location}</dd>
                          </div>
                        )}
                        <div className="flex justify-between gap-2">
                          <dt className="vscode-text-tertiary">Applications</dt>
                          <dd className="vscode-text-primary font-semibold tabular-nums">{jobApps.length}</dd>
                        </div>
                        <div className="flex justify-between gap-2">
                          <dt className="vscode-text-tertiary">Active</dt>
                          <dd className="vscode-text-primary tabular-nums">{activeCount}</dd>
                        </div>
                        {staleCount > 0 && (
                          <div className="flex justify-between gap-2">
                            <dt className="vscode-text-tertiary">Stale</dt>
                            <dd className="text-amber-600 dark:text-amber-400 font-semibold tabular-nums">{staleCount} ⚠</dd>
                          </div>
                        )}
                        <div className="flex justify-between gap-2">
                          <dt className="vscode-text-tertiary">Opened</dt>
                          <dd className="vscode-text-secondary tabular-nums">
                            {job.opened_at || job.created_at
                              ? formatDateTimeGmt(job.opened_at ?? job.created_at)
                              : '—'}
                          </dd>
                        </div>
                      </dl>
                      <div className="mt-2 pt-2 border-t border-[var(--vscode-border-primary)]/40">
                        {staleCount > 0 ? (
                          <p className="text-[11px] text-amber-600 dark:text-amber-400 mb-1.5">{staleCount} application{staleCount !== 1 ? 's' : ''} need attention.</p>
                        ) : activeCount > 0 ? (
                          <p className="text-[11px] vscode-text-tertiary mb-1.5">{activeCount} active in pipeline.</p>
                        ) : (
                          <p className="text-[11px] vscode-text-tertiary mb-1.5">No active applications.</p>
                        )}
                        <Link to={`/jobs/${job.id}`} className="text-xs text-[var(--vscode-primary)] hover:underline">
                          [↗ Open job]
                        </Link>
                      </div>
                    </CandidateCollapsiblePanel>
                  );
                })}
              </aside>
            </div>

            {/* Application detail modal — reuses same rich modal */}
            {appModal && (
              <CandidateApplicationDetailModal
                app={appModal}
                teamMembers={[]}
                onClose={() => setAppModal(null)}
              />
            )}
          </>
        );
      })()}

      {/* ── Applications tab — grouped by job ───────────────── */}
      {/* ── Applications tab — mirrors CandidateApplicationsPanel ─ */}
      {activeTab === 'applications' && (() => {
        const sorted = [...allApplications].sort(
          (a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()
        );
        const TERMINAL_STS = ['placed','rejected','closed','withdrawn'];
        const active    = sorted.filter((a) => !TERMINAL_STS.includes(a.status)).length;
        const placed    = sorted.filter((a) => a.status === 'placed').length;
        const rejected  = sorted.filter((a) => a.status === 'rejected').length;
        const withdrawn = sorted.filter((a) => a.status === 'withdrawn').length;
        const closed    = sorted.filter((a) => a.status === 'closed').length;
        const fitVals   = sorted.map((a) => a.fitment_score).filter((n): n is number => n != null);
        const avgFit    = fitVals.length ? Math.round(fitVals.reduce((s, n) => s + n, 0) / fitVals.length) : null;
        const maxFit    = fitVals.length ? Math.max(...fitVals) : null;
        const uniqueCands = new Set(sorted.map((a) => a.candidate_id).filter(Boolean)).size;
        const uniqueJobs  = new Set(sorted.map((a) => a.job_id).filter(Boolean)).size;
        const lastSub   = sorted[0]?.submitted_at ?? null;
        const stKeys    = sorted.map((a) => new Date(a.status_updated_at).getTime());
        const lastStage = stKeys.length ? new Date(Math.max(...stKeys)).toISOString() : null;
        const withFit   = sorted.filter((a) => a.fitment_score != null).length;
        const withAts   = sorted.filter((a) => (a as unknown as { optimization?: unknown }).optimization != null).length;
        const byStatus: Record<string, number> = {};
        for (const a of sorted) byStatus[a.status] = (byStatus[a.status] ?? 0) + 1;

        const appAgo = (iso: string) => {
          const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
          if (d < 1) return 'today'; if (d === 1) return 'yesterday';
          if (d < 14) return `${d}d ago`; if (d < 60) return `${Math.floor(d / 7)}w ago`;
          return `${Math.floor(d / 30)}mo ago`;
        };

        const DT_CLS = 'vscode-text-secondary pr-2 min-w-0 text-sm';
        const DD_CLS = 'vscode-text-primary min-w-0 break-words text-sm';

        return (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(300px,28%)] gap-3 lg:gap-5 items-start">
            {/* ── Left: summary panel (mirrors CandidateApplicationsPanel) ── */}
            <div className="ggj-param-group overflow-hidden">
              {/* Tab toolbar */}
              <div className="ggj-profile-toolbar">
                <span className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider flex-1">
                  Applications ({sorted.length}) <span className="text-[var(--vscode-primary)] text-[10px] ml-2">●</span>
                </span>
                <Link to={`/applications/new?clientId=${client.id}`} className="ggj-toolbar-btn">[↗ Open in hub]</Link>
              </div>

              {sorted.length === 0 ? (
                <div className="ggj-profile-section-body py-10 text-center">
                  <p className="text-sm vscode-text-secondary font-medium">No applications yet for this client.</p>
                  <p className="text-xs vscode-text-tertiary mt-1">
                    Submit a candidate to one of this client&apos;s jobs to see it here.
                  </p>
                </div>
              ) : (
                <>
                  {/* ── Pipeline summary ── */}
                  <div className="ggj-profile-section-h">Pipeline summary</div>
                  <div className="ggj-profile-section-body">
                    <dl className="ggj-attr-grid">
                      <dt className={DT_CLS}>Total applications</dt><dd className={DD_CLS}>{sorted.length}</dd>
                      <dt className={DT_CLS}>Active in pipeline</dt><dd className={DD_CLS}>{active}</dd>
                      <dt className={DT_CLS}>Placed</dt><dd className={DD_CLS}>{placed}</dd>
                      <dt className={DT_CLS}>Rejected</dt><dd className={DD_CLS}>{rejected}</dd>
                      <dt className={DT_CLS}>Withdrawn / closed</dt><dd className={DD_CLS}>{withdrawn} / {closed}</dd>
                      <dt className={DT_CLS}>Unique candidates</dt><dd className={DD_CLS}>{uniqueCands}</dd>
                      <dt className={DT_CLS}>Open positions</dt><dd className={DD_CLS}>{uniqueJobs}</dd>
                      <dt className={DT_CLS}>Latest submission</dt><dd className={DD_CLS}>{lastSub ? appAgo(lastSub) : '—'}</dd>
                      <dt className={DT_CLS}>Last stage change</dt><dd className={DD_CLS}>{lastStage ? appAgo(lastStage) : '—'}</dd>
                    </dl>
                  </div>

                  {/* ── Stage distribution ── */}
                  <div className="ggj-profile-section-h">Stage distribution</div>
                  <div className="ggj-profile-section-body">
                    <dl className="ggj-attr-grid">
                      {Object.entries(byStatus).sort((a, b) => b[1] - a[1]).map(([st, n]) => (
                        <>
                          <dt key={`dt-${st}`} className={DT_CLS}>{st.charAt(0).toUpperCase() + st.slice(1)}</dt>
                          <dd key={`dd-${st}`} className={`${DD_CLS} inline-flex items-center gap-2`}>
                            <span className="font-semibold tabular-nums">{n}</span>
                            <StatusBadge status={st as Application['status']} />
                          </dd>
                        </>
                      ))}
                    </dl>
                  </div>

                  {/* ── Fitment & scoring ── */}
                  <div className="ggj-profile-section-h">Fitment &amp; scoring</div>
                  <div className="ggj-profile-section-body">
                    <dl className="ggj-attr-grid">
                      <dt className={DT_CLS}>Avg. role-fit score</dt><dd className={DD_CLS}>{avgFit != null ? `${avgFit} / 100` : '—'}</dd>
                      <dt className={DT_CLS}>Highest fitment score</dt><dd className={DD_CLS}>{maxFit != null ? `${maxFit} / 100` : '—'}</dd>
                      <dt className={DT_CLS}>With fitment data</dt><dd className={DD_CLS}>{withFit} of {sorted.length}</dd>
                      <dt className={DT_CLS}>ATS optimized</dt><dd className={DD_CLS}>{withAts} of {sorted.length}</dd>
                    </dl>
                  </div>

                  {/* ── Submitted applications — multi-value list (1:many by job) ── */}
                  <div className="ggj-profile-section-h">Submitted applications</div>
                  <div className="ggj-profile-section-body">
                    <dl className="ggj-attr-grid">
                      <dt className={`${DT_CLS} lg:col-span-1`}>Submitted applications</dt>
                      <dd className={`${DD_CLS} lg:col-span-3 space-y-1.5`}>
                        {sorted.map((app) => (
                          <button
                            key={app.id}
                            type="button"
                            onClick={() => setAppModal(app)}
                            className="flex items-baseline gap-1.5 text-left text-sm text-[var(--vscode-primary)] hover:underline w-full"
                          >
                            <span className="font-medium truncate">{app.job?.title ?? 'No role'}</span>
                            <span className="vscode-text-tertiary text-xs shrink-0">›</span>
                            <span className="vscode-text-secondary text-xs truncate">{app.candidate?.full_name ?? '—'}</span>
                            <span className="vscode-text-tertiary text-[10px] shrink-0 ml-auto tabular-nums">
                              {formatDateTimeGmt(app.submitted_at)}
                            </span>
                          </button>
                        ))}
                      </dd>
                    </dl>
                  </div>
                </>
              )}
            </div>

            {/* ── Right rail: per-application action cards (mirrors candidate tab right panel) ── */}
            <aside className="ggj-rail-aside space-y-2 lg:sticky lg:top-4 self-start">
              {sorted.map((app) => {
                const daysInStage = Math.floor((Date.now() - new Date(app.status_updated_at).getTime()) / 86400000);
                const isStale = !TERMINAL_STS.includes(app.status) && daysInStage >= 5;
                const needsAction = isStale || app.status === 'submitted' || app.status === 'screening';
                return (
                  <CandidateCollapsiblePanel
                    key={app.id}
                    storageKey={`${client.id}-app-card-${app.id}`}
                    title={(app.candidate?.full_name ?? 'Candidate').toUpperCase()}
                  >
                    <dl className="space-y-1 text-xs">
                      <div className="flex justify-between gap-2">
                        <dt className="vscode-text-tertiary">Job</dt>
                        <dd className="vscode-text-primary font-medium text-right truncate max-w-[160px]">
                          {app.job?.title ?? '—'}
                        </dd>
                      </div>
                      <div className="flex justify-between gap-2 items-center">
                        <dt className="vscode-text-tertiary">Stage</dt>
                        <dd><StatusBadge status={app.status} /></dd>
                      </div>
                      <div className="flex justify-between gap-2">
                        <dt className="vscode-text-tertiary">In stage</dt>
                        <dd className={isStale ? 'font-semibold text-amber-600 dark:text-amber-400' : 'vscode-text-primary'}>
                          {daysInStage === 0 ? 'today' : daysInStage === 1 ? 'yesterday' : `${daysInStage}d`}
                          {isStale ? ' ⚠' : ''}
                        </dd>
                      </div>
                      {app.fitment_score != null && (
                        <div className="flex justify-between gap-2">
                          <dt className="vscode-text-tertiary">Fitment</dt>
                          <dd className="vscode-text-primary">{Math.round(app.fitment_score)}%</dd>
                        </div>
                      )}
                    </dl>
                    <div className="mt-2 pt-2 border-t border-[var(--vscode-border-primary)]/40">
                      {needsAction ? (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400 mb-1.5">Action required.</p>
                      ) : (
                        <p className="text-[11px] vscode-text-tertiary mb-1.5">No immediate action needed.</p>
                      )}
                      <div className="flex items-center justify-between gap-2">
                        <button
                          type="button"
                          className="text-xs text-[var(--vscode-primary)] hover:underline"
                          onClick={() => setAppModal(app)}
                        >
                          [↗ View details]
                        </button>
                        <Link
                          to={`/applications/${app.id}`}
                          className="text-xs text-[var(--vscode-primary)] hover:underline"
                        >
                          [Open application]
                        </Link>
                      </div>
                    </div>
                  </CandidateCollapsiblePanel>
                );
              })}
            </aside>
          </div>
        );
      })()}

      {/* Application detail modal — reuses the same rich modal as Candidate > Applications tab */}
      {appModal && (
        <CandidateApplicationDetailModal
          app={appModal}
          teamMembers={[]}
          onClose={() => setAppModal(null)}
        />
      )}

      {/* ── Emails tab ───────────────────────────────────────── */}
      {activeTab === 'emails' && (() => {
        const needFollowUp = emailThreads.filter((t) => {
          const ctaResp = (t.cta_response as string | undefined);
          return !ctaResp && t.open_count === 0 && t.sent_at;
        });
        const awaiting = emailThreads.filter((t) => !(t.cta_response as string | undefined));
        return (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(300px,28%)] gap-3 lg:gap-5 items-start">
          {/* Main emails table */}
          <div className="card overflow-hidden p-0">
            <div className="ggj-profile-toolbar">
              <span className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider flex-1">
                {emailTotal} email thread{emailTotal !== 1 ? 's' : ''} · {needFollowUp.length} need follow-up
              </span>
            </div>
          {emailThreads.length === 0 ? (
            <p className="text-sm vscode-text-tertiary py-8 text-center">No emails yet for this client</p>
          ) : (
            <>
            {/* horizontal scroll wrapper */}
            <div className="overflow-x-auto">
            <table className="ggj-data-table min-w-[860px]">
              <thead>
                <tr style={{ background: 'color-mix(in srgb, var(--vscode-primary) 8%, var(--vscode-bg-secondary))' }}>
                  <th className="px-4 py-2.5 w-10 border-b vscode-border">
                    <input
                      type="checkbox"
                      ref={emailCheckRef}
                      checked={emailThreadsFiltered.length > 0 && selectedEmailIds.size === emailThreadsFiltered.length}
                      onChange={() => {
                        if (selectedEmailIds.size === emailThreadsFiltered.length) setSelectedEmailIds(new Set());
                        else setSelectedEmailIds(new Set(emailThreadsFiltered.map((t) => t.id)));
                      }}
                      className="rounded border-[var(--vscode-border-input)]"
                    />
                  </th>
                  <th className="px-4 py-2.5 text-left border-b vscode-border">Recipient</th>
                  <th className="px-4 py-2.5 text-left border-b vscode-border">Subject / Candidate · Role</th>
                  <th className="px-4 py-2.5 text-left border-b vscode-border">Response</th>
                  <th className="px-4 py-2.5 text-left border-b vscode-border">Opens</th>
                  <th className="px-4 py-2.5 text-left border-b vscode-border">Sent</th>
                  <th className="px-4 py-2.5 text-left border-b vscode-border">Sent By</th>
                  <th className="px-4 py-2.5 text-left border-b vscode-border">Stake Holder</th>
                  <th className="px-4 py-2.5 text-right border-b vscode-border w-24">Action</th>
                </tr>
              </thead>
              <tbody>
                {emailThreadsFiltered.map((thread) => {
                  const app = thread.application as { id?: string; candidate?: { full_name: string }; job?: { title: string }; client?: { name: string }; assigned_user?: { display_name?: string; email?: string } } | undefined;
                  const candidateJob = [app?.candidate?.full_name, app?.job?.title].filter(Boolean).join(' · ') || '—';
                  const ctaResponse = thread.cta_response as CTAAction | undefined;
                  const isResponded = !!ctaResponse;
                  const needsFollowUp = !isResponded && thread.open_count === 0 && thread.sent_at;
                  const returnUrl = `/clients/${id}?tab=emails`;
                  const openThread = () => navigate(`/emails/thread/${thread.id}`, { state: { returnTo: returnUrl } });
                  const showRecipientInfo = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                    setRecipientPopover({ thread, anchor: { top: rect.bottom + 4, left: rect.left } });
                  };
                  const sentByDisplay = (thread as { sent_by_user?: { display_name?: string; email?: string } }).sent_by_user?.display_name
                    ?? (thread as { sent_by_user?: { display_name?: string; email?: string } }).sent_by_user?.email ?? '—';
                  const stakeHolderDisplay = app?.assigned_user?.display_name ?? app?.assigned_user?.email ?? '—';
                  return (
                    <tr
                      key={thread.id}
                      className={`transition-colors ${selectedEmailIds.has(thread.id) ? 'bg-brand-500/10' : ''}`}
                    >
                      <td className="px-4 py-3 w-10" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedEmailIds.has(thread.id)}
                          onChange={() => {
                            const next = new Set(selectedEmailIds);
                            if (next.has(thread.id)) next.delete(thread.id);
                            else next.add(thread.id);
                            setSelectedEmailIds(next);
                          }}
                          className="rounded border-[var(--vscode-border-input)]"
                        />
                      </td>
                      <td
                        className="px-4 py-3 cursor-pointer hover:text-brand-400"
                        onClick={showRecipientInfo}
                        title="Click to view recipient info"
                      >
                        <p className="font-medium vscode-text-primary">{thread.recipient_name || thread.recipient_email}</p>
                        <p className="text-xs vscode-text-tertiary truncate max-w-[180px]">{thread.recipient_email}</p>
                      </td>
                      <td className="px-4 py-3 cursor-pointer" onClick={openThread}>
                        <p className="vscode-text-secondary truncate max-w-[220px] hover:text-brand-400">{thread.subject}</p>
                        <p className="text-xs vscode-text-tertiary truncate max-w-[220px]">{candidateJob}</p>
                      </td>
                      <td className="px-4 py-3 cursor-pointer" onClick={openThread}>
                        {isResponded && ctaResponse ? (
                          <div className="flex items-center gap-1.5">
                            {CTA_ICON[ctaResponse]}
                            <span className="text-xs vscode-text-secondary">{CTA_LABEL[ctaResponse]}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5 vscode-text-tertiary" />
                            <span className="text-xs vscode-text-tertiary">Awaiting reply</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 cursor-pointer" onClick={openThread}>
                        <div className="flex items-center gap-1.5 text-xs vscode-text-tertiary">
                          <Eye className="w-3 h-3" />
                          {thread.open_count > 0 ? (
                            <span className="vscode-text-secondary">{thread.open_count}× opened</span>
                          ) : isResponded ? (
                            <span className="vscode-text-secondary">Engaged</span>
                          ) : (
                            'Not opened'
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs vscode-text-tertiary cursor-pointer" onClick={openThread}>
                        {thread.sent_at ? formatDateTimeGmt(thread.sent_at) : '—'}
                      </td>
                      <td className="px-4 py-3 text-xs vscode-text-tertiary">{sentByDisplay}</td>
                      <td className="px-4 py-3 text-xs vscode-text-tertiary">{stakeHolderDisplay}</td>
                      <td className="px-4 py-3 text-right">
                        {needsFollowUp ? (
                          <>
                            <Link to={`/emails/compose?applicationId=${thread.application_id}`} state={{ returnTo: returnUrl }} className="text-xs text-amber-400 hover:text-amber-300 mr-2">[Follow up]</Link>
                            <Link to={`/emails/compose?applicationId=${thread.application_id}`} state={{ returnTo: returnUrl }} className="vscode-text-secondary hover:vscode-text-primary inline-flex"><ChevronRight className="w-4 h-4" /></Link>
                          </>
                        ) : (
                          <>
                            <Link to={`/emails/thread/${thread.id}`} state={{ returnTo: returnUrl }} className="text-xs text-brand-400 hover:text-brand-300 mr-2">[View Email]</Link>
                            <Link to={`/emails/thread/${thread.id}`} state={{ returnTo: returnUrl }} className="vscode-text-secondary hover:vscode-text-primary inline-flex"><ChevronRight className="w-4 h-4" /></Link>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>{/* end overflow-x-auto */}
            <TablePagination
              page={emailPage}
              pageSize={emailPageSize}
              totalItems={emailTotal}
              onPageChange={setEmailPage}
              onPageSizeChange={(size) => { setEmailPageSize(size); setEmailPage(1); }}
            />
            </>
          )}
          </div>

          {/* Right rail */}
          <aside className="ggj-rail-aside space-y-2 lg:sticky lg:top-4 self-start">
            {needFollowUp.length > 0 && (
              <CandidateCollapsiblePanel storageKey={`${client.id}-follow-up`} title={`Follow-up needed (${needFollowUp.length})`}>
                <div className="space-y-2">
                  {needFollowUp.slice(0, 8).map((t) => {
                    const app = t.application as { id?: string; candidate?: { full_name: string }; job?: { title: string } } | undefined;
                    const returnUrl = `/clients/${id}?tab=emails`;
                    return (
                      <div key={t.id} className="text-xs border-b vscode-border pb-2 last:border-0 last:pb-0">
                        <p className="font-medium vscode-text-primary">{t.recipient_name || t.recipient_email}</p>
                        {app?.candidate?.full_name && <p className="vscode-text-secondary">{app.candidate.full_name}{app?.job?.title ? ` · ${app.job.title}` : ''}</p>}
                        <p className="vscode-text-tertiary truncate">{t.subject}</p>
                        <p className="text-amber-600 dark:text-amber-400 mt-0.5">
                          Not opened · {t.sent_at ? formatDateTimeGmt(t.sent_at) : '—'}
                        </p>
                        <div className="flex gap-3 mt-1.5 flex-wrap">
                          <Link to={`/emails/thread/${t.id}`} state={{ returnTo: returnUrl }} className="text-[var(--vscode-primary)] hover:underline">[View email]</Link>
                          <Link to={`/emails/compose?applicationId=${t.application_id}`} state={{ returnTo: returnUrl }} className="text-amber-600 dark:text-amber-400 hover:underline">[Follow up →]</Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CandidateCollapsiblePanel>
            )}

            {awaiting.length > 0 && (
              <CandidateCollapsiblePanel storageKey={`${client.id}-awaiting-email`} title={`Awaiting response (${awaiting.length})`}>
                <div className="space-y-2">
                  {awaiting.slice(0, 6).map((t) => {
                    const app = t.application as { candidate?: { full_name: string }; job?: { title: string } } | undefined;
                    const returnUrl = `/clients/${id}?tab=emails`;
                    return (
                      <div key={t.id} className="text-xs border-b vscode-border pb-2 last:border-0 last:pb-0">
                        <p className="font-medium vscode-text-primary">{t.recipient_name || t.recipient_email}</p>
                        {app?.candidate?.full_name && <p className="vscode-text-secondary">{app.candidate.full_name}{app.job?.title ? ` · ${app.job.title}` : ''}</p>}
                        <div className="flex items-center gap-1 mt-1 text-[11px] vscode-text-tertiary">
                          <Eye className="w-3 h-3" />
                          {t.open_count > 0 ? `${t.open_count}× opened` : 'Not opened'}
                        </div>
                        <div className="flex gap-3 mt-1.5">
                          <Link to={`/emails/thread/${t.id}`} state={{ returnTo: returnUrl }} className="text-[var(--vscode-primary)] hover:underline">[View email]</Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CandidateCollapsiblePanel>
            )}

            <CandidateCollapsiblePanel storageKey={`${client.id}-email-stats`} title="Email summary">
              <dl className="space-y-1.5 text-xs">
                <div className="flex justify-between"><dt className="vscode-text-secondary">Total sent</dt><dd className="font-semibold vscode-text-primary tabular-nums">{emailTotal}</dd></div>
                <div className="flex justify-between"><dt className="vscode-text-secondary">Responded</dt><dd className="font-semibold vscode-text-primary tabular-nums">{emailTotal - awaiting.length}</dd></div>
                <div className="flex justify-between"><dt className="vscode-text-secondary">Awaiting reply</dt><dd className="font-semibold text-amber-600 dark:text-amber-400 tabular-nums">{awaiting.length}</dd></div>
                <div className="flex justify-between"><dt className="vscode-text-secondary">Needs follow-up</dt><dd className="font-semibold text-red-500 tabular-nums">{needFollowUp.length}</dd></div>
              </dl>
            </CandidateCollapsiblePanel>
          </aside>
        </div>
        );
      })()}

      {/* Contacts and Notes tabs removed — surfaced in Overview right rail */}

      {false && (
        <div className="space-y-4 max-w-3xl">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">[Contacts]</h2>
            <button className="btn-primary" onClick={() => setContactFormOpen(true)}>
              <Plus className="w-4 h-4" /> [Add contact]
            </button>
          </div>

          {contactFormOpen && (
            <div className="card p-5 space-y-4 border-brand-700">
              <h3 className="text-sm font-semibold vscode-text-primary">New contact</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label mb-1">Full name *</label>
                  <input className="input w-full" value={contactForm.full_name} onChange={(e) => setContactForm((f) => ({ ...f, full_name: e.target.value }))} placeholder="Priya Sharma" />
                </div>
                <div>
                  <label className="label mb-1">Title</label>
                  <input className="input w-full" value={contactForm.title} onChange={(e) => setContactForm((f) => ({ ...f, title: e.target.value }))} placeholder="HR Manager" />
                </div>
                <div>
                  <label className="label mb-1">Email</label>
                  <input className="input w-full" type="email" value={contactForm.email} onChange={(e) => setContactForm((f) => ({ ...f, email: e.target.value }))} />
                </div>
                <div>
                  <label className="label mb-1">Phone</label>
                  <input className="input w-full" value={contactForm.phone} onChange={(e) => setContactForm((f) => ({ ...f, phone: e.target.value }))} />
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <input type="checkbox" id="is_primary" checked={contactForm.is_primary} onChange={(e) => setContactForm((f) => ({ ...f, is_primary: e.target.checked }))} className="rounded border-[var(--vscode-border-input)]" />
                  <label htmlFor="is_primary" className="text-sm vscode-text-secondary">Primary contact</label>
                </div>
              </div>
              <div>
                <label className="label mb-1">Notes</label>
                <textarea className="input w-full" rows={2} value={contactForm.notes} onChange={(e) => setContactForm((f) => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="flex gap-2">
                <button className="btn-primary" onClick={handleSaveContact} disabled={savingContact || !contactForm.full_name.trim()}>
                  {savingContact ? 'Saving…' : 'Save'}
                </button>
                <button className="btn-secondary" onClick={() => setContactFormOpen(false)}>Cancel</button>
              </div>
            </div>
          )}

          {contacts.length === 0 && !contactFormOpen ? (
            <div className="card py-10 text-center">
              <Users className="w-10 h-10 vscode-text-tertiary mx-auto mb-3" />
              <p className="vscode-text-tertiary text-sm">No contacts yet.</p>
            </div>
          ) : (
            <div className="card overflow-hidden p-0">
              <table className="ggj-data-table">
                <thead>
                  <tr className="border-b vscode-border">
                    <th className="px-4 py-3 text-left">[Name]</th>
                    <th className="px-4 py-3 text-left">[Title]</th>
                    <th className="px-4 py-3 text-left">[Email]</th>
                    <th className="px-4 py-3 text-left">[Phone]</th>
                    <th className="px-4 py-3 text-right">[Actions]</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.map((contact) => (
                    <tr key={contact.id}>
                      <td className="px-4 py-3">
                        <span className="font-medium vscode-text-primary">{contact.full_name}</span>
                        {contact.is_primary && (
                          <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-brand-900/50 text-brand-300">primary</span>
                        )}
                      </td>
                      <td className="px-4 py-3 vscode-text-secondary text-sm">{contact.title ?? '—'}</td>
                      <td className="px-4 py-3 text-sm">
                        {contact.email
                          ? <a href={`mailto:${contact.email}`} className="text-brand-400 hover:underline">{contact.email}</a>
                          : <span className="vscode-text-tertiary">—</span>
                        }
                      </td>
                      <td className="px-4 py-3 vscode-text-secondary text-sm">{contact.phone ?? '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={async () => {
                            await clientContactService.delete(contact.id);
                            setContacts((prev) => prev.filter((c) => c.id !== contact.id));
                          }}
                          className="text-red-400 hover:text-red-300 text-xs"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {false && (
        <div className="space-y-4 max-w-3xl">
          <h2 className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">[Client Notes]</h2>
          <div className="flex gap-2">
            <textarea
              className="input flex-1 resize-none"
              rows={3}
              placeholder="Add a note about this client…"
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
            />
            <button className="btn-primary self-end" onClick={handleAddNote} disabled={savingNote || !noteInput.trim()}>
              {savingNote ? 'Saving…' : 'Add'}
            </button>
          </div>
          {clientNotes.length === 0 ? (
            <div className="card py-10 text-center">
              <StickyNote className="w-10 h-10 vscode-text-tertiary mx-auto mb-3" />
              <p className="vscode-text-tertiary text-sm">No notes yet.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {clientNotes.map((note) => (
                <div key={note.id} className={`card p-4 ${note.pinned ? 'border-amber-700/50' : ''}`}>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm vscode-text-secondary whitespace-pre-wrap">{note.body}</p>
                      <p className="text-xs vscode-text-tertiary mt-2">
                        {note.created_by ?? 'Unknown'} · {formatDateTimeGmt(note.created_at)}
                      </p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <button
                        onClick={async () => {
                          await clientNoteService.togglePin(note.id, !note.pinned);
                          setClientNotes((prev) => prev.map((n) => n.id === note.id ? { ...n, pinned: !n.pinned } : n));
                        }}
                        className={`p-1 rounded ${note.pinned ? 'text-amber-400' : 'vscode-text-tertiary hover:text-amber-400'}`}
                        title="Pin / unpin"
                      >
                        <Pin className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={async () => {
                          await clientNoteService.delete(note.id);
                          setClientNotes((prev) => prev.filter((n) => n.id !== note.id));
                        }}
                        className="p-1 rounded vscode-text-tertiary hover:text-red-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Onboarding tab ───────────────────────────────────── */}
      {activeTab === 'onboarding' && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_min(340px,32%)] gap-3 lg:gap-5 items-start">
          {/* Left: attribute groups */}
          <div className="space-y-3">

            {/* Group 1 — Compliance & Readiness */}
            <div className="ggj-param-group overflow-hidden">
              <div className="ggj-param-group-h flex items-center justify-between gap-3">
                <span>Compliance &amp; Readiness</span>
                {isClientReadyForRecruitment(client) ? (
                  <span className="text-[10px] font-normal normal-case tracking-normal text-emerald-600 dark:text-emerald-400">✓ Ready to recruit</span>
                ) : (
                  <span className="text-[10px] font-normal normal-case tracking-normal text-amber-600 dark:text-amber-400">⚠ Onboarding incomplete</span>
                )}
              </div>
              <div className="ggj-param-group-body">
                <dl className="ggj-attr-grid">
                  <dt className="vscode-text-secondary pr-2 min-w-0">Recruitment status</dt>
                  <dd className="vscode-text-primary min-w-0 break-words">
                    {isClientReadyForRecruitment(client) ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400 font-semibold">✓ Ready to recruit</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-amber-700 dark:text-amber-300 font-semibold">⚠ Onboarding in progress</span>
                    )}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Recruitment gate</dt>
                  <dd className="vscode-text-primary min-w-0 break-words">
                    {isClientReadyForRecruitment(client) ? (
                      <span className="text-xs text-emerald-700 dark:text-emerald-400">✓ All gates passed</span>
                    ) : (
                      <span className="text-xs text-amber-700 dark:text-amber-300">⚠ Documents pending</span>
                    )}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Document gate</dt>
                  <dd className="vscode-text-primary min-w-0 break-words">
                    <span className="text-xs vscode-text-primary">
                      {(() => {
                        const slugs = ['executed_msa_sow', 'company_registration', 'tax_compliance'];
                        const approved = slugs.filter(s => client[`doc_approved_${s}` as keyof typeof client]);
                        return `${approved.length} / ${slugs.length} docs approved`;
                      })()}
                    </span>
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Onboarding started</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {client.created_at
                      ? formatDateTimeGmt(client.created_at)
                      : <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Last updated</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).updated_at
                      ? formatDateTimeGmt((client as Record<string, unknown>).updated_at as string)
                      : <span className="vscode-text-tertiary">—</span>}
                  </dd>
                </dl>
              </div>
            </div>

            {/* Group 2 — Engagement & Consent */}
            <div className="ggj-param-group overflow-hidden">
              <div className="ggj-param-group-h">Engagement &amp; Consent</div>
              <div className="ggj-param-group-body">
                <dl className="ggj-attr-grid">
                  <dt className="vscode-text-secondary pr-2 min-w-0">Consent accepted</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {client.client_consent_accepted_at
                      ? formatDateTimeGmt(client.client_consent_accepted_at)
                      : <span className="vscode-text-tertiary">No consent on record</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Consent version</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">{client.client_consent_version ?? <span className="vscode-text-tertiary">—</span>}</dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Agreement label</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">{client.commercial_agreement_label ?? <span className="vscode-text-tertiary">—</span>}</dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">MOU / SOW reference</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).mou_reference as string ?? <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Contract start</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).contract_start_date
                      ? formatDateOrDateTimeGmt((client as Record<string, unknown>).contract_start_date as string)
                      : <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Contract end</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).contract_end_date
                      ? formatDateOrDateTimeGmt((client as Record<string, unknown>).contract_end_date as string)
                      : <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Terms summary</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).terms_summary as string ?? <span className="vscode-text-tertiary">—</span>}
                  </dd>
                </dl>
              </div>
            </div>

            {/* Group 3 — Commercial Terms */}
            <div className="ggj-param-group overflow-hidden">
              <div className="ggj-param-group-h">Commercial Terms</div>
              <div className="ggj-param-group-body">
                <dl className="ggj-attr-grid">
                  <dt className="vscode-text-secondary pr-2 min-w-0">Billing type</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {client.billing_type ?? <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Payment terms</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).payment_terms as string ?? <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Replacement window</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {client.replacement_window_days != null
                      ? `${client.replacement_window_days} days`
                      : <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Guarantee period</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {client.guarantee_period_days != null
                      ? `${client.guarantee_period_days} days`
                      : <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Invoice currency</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).invoice_currency as string ?? <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Credit limit</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).credit_limit != null
                      ? `₹ ${Number((client as Record<string, unknown>).credit_limit).toLocaleString('en-IN')}`
                      : <span className="vscode-text-tertiary">—</span>}
                  </dd>
                </dl>
              </div>
            </div>

            {/* Group 4 — Pending & Follow-up */}
            <div className="ggj-param-group overflow-hidden">
              <div className="ggj-param-group-h">Pending &amp; Follow-up</div>
              <div className="ggj-param-group-body space-y-2">
                <p className="text-xs vscode-text-secondary">Outstanding request or reminder sent to the client regarding missing documents or actions.</p>
                <dl className="ggj-attr-grid">
                  <dt className="vscode-text-secondary pr-2 min-w-0">Pending request note</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {client.compliance_pending_request_note
                      ? <span className="text-amber-700 dark:text-amber-300">{client.compliance_pending_request_note}</span>
                      : <span className="vscode-text-tertiary">No pending requests</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Follow-up due</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).follow_up_due_date
                      ? formatDateOrDateTimeGmt((client as Record<string, unknown>).follow_up_due_date as string)
                      : <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Assigned recruiter</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {client.account_manager_name ?? <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Escalation flag</dt>
                  <dd className="vscode-text-primary min-w-0 break-words">
                    {(client as Record<string, unknown>).escalation_flag
                      ? <span className="text-xs text-red-600 dark:text-red-400 font-semibold">⚑ Escalated</span>
                      : <span className="text-xs vscode-text-tertiary">None</span>}
                  </dd>
                </dl>
              </div>
            </div>

            {/* Group 5 — Identity & Audit */}
            <div className="ggj-param-group overflow-hidden">
              <div className="ggj-param-group-h">Identity &amp; Audit</div>
              <div className="ggj-param-group-body">
                <dl className="ggj-attr-grid">
                  <dt className="vscode-text-secondary pr-2 min-w-0">Client ID</dt>
                  <dd className="vscode-text-secondary pr-2 min-w-0 font-mono text-xs">{client.id.slice(0, 16)}…</dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">CRM / ERP ref</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).crm_reference as string ?? <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Created by</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).created_by as string ?? <span className="vscode-text-tertiary">—</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Created</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {formatDateTimeGmt(client.created_at)}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Record source</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).record_source as string ?? <span className="vscode-text-tertiary">Manual entry</span>}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Data region</dt>
                  <dd className="vscode-text-primary min-w-0 break-words text-xs">
                    {(client as Record<string, unknown>).data_region as string ?? <span className="vscode-text-tertiary">—</span>}
                  </dd>
                </dl>
              </div>
            </div>

          </div>

          {/* Right rail: compliance actions + doc panels */}
          <aside className="ggj-rail-aside space-y-2 lg:sticky lg:top-4 self-start">

            {/* Quick actions */}
            <CandidateCollapsiblePanel storageKey={`${client.id}-onboard-actions`} title="Quick actions">
              <div className="space-y-2 text-xs">
                <button type="button" className="w-full text-left ggj-toolbar-btn text-xs"
                  onClick={() => setActiveTab('overview')}>[← Back to overview]</button>
                <button type="button" className="w-full text-left ggj-toolbar-btn text-xs"
                  onClick={() => setActiveTab('applications')}>[→ View applications]</button>
                {!isClientReadyForRecruitment(client) && (
                  <p className="text-amber-600 dark:text-amber-400 text-[11px] mt-1">
                    ⚠ Recruiting is blocked until all required documents are approved or the client is manually marked ready.
                  </p>
                )}
              </div>
            </CandidateCollapsiblePanel>

            {/* Compliance checklist */}
            <CandidateCollapsiblePanel storageKey={`${client.id}-onboard-checklist`} title="Compliance checklist">
              <ul className="space-y-2 text-xs">
                {[
                  { label: 'Engagement consent accepted', done: !!client.client_consent_accepted_at },
                  { label: 'Consent version recorded', done: !!client.client_consent_version },
                  { label: 'Commercial agreement on file', done: !!client.commercial_agreement_label },
                  { label: 'Documents approved', done: isClientReadyForRecruitment(client) },
                  { label: 'Primary contact set', done: !!client.contact_email },
                  { label: 'Account manager assigned', done: !!client.account_manager_name },
                  { label: 'No pending requests', done: !client.compliance_pending_request_note },
                ].map((item) => (
                  <li key={item.label} className="flex items-center gap-2">
                    <span className={item.done ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}>
                      {item.done ? '✓' : '○'}
                    </span>
                    <span className={item.done ? 'vscode-text-secondary line-through' : 'vscode-text-primary'}>{item.label}</span>
                  </li>
                ))}
              </ul>
            </CandidateCollapsiblePanel>

            {/* Document panels: engagement declaration + uploads + review */}
            {user.orgId && (
              <ClientOnboardingPanel
                client={client}
                user={{ orgId: user.orgId, name: user.name }}
                onClientUpdated={(c) => setClient(c)}
                compact
              />
            )}

          </aside>
        </div>
      )}

      {activeTab === 'parameters' && (
        <div className="card p-6">
          {/* Toolbar */}
          <div className="ggj-profile-toolbar mb-5">
            <span className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">[Parameters]</span>
            <div className="flex flex-wrap gap-2">
              {!paramsEditing ? (
                <button
                  type="button"
                  onClick={() => setParamsEditing(true)}
                  className={`${detailTbBtn} hover:bg-slate-200/90 dark:hover:bg-gray-700`}
                >
                  <Pencil className="h-4 w-4 shrink-0 text-[var(--vscode-primary)]" aria-hidden />
                  <span className="text-slate-700 dark:text-gray-300">Edit</span>
                </button>
              ) : (
                <>
                  <button type="button" onClick={handleParamsSave} className={`${detailTbBtn} ${detailTbNeutral}`}>
                    <Save className="gg-toolbar-ic gg-toolbar-ic--violet shrink-0" aria-hidden />
                    <span>[Save]</span>
                  </button>
                  <button type="button" onClick={handleParamsCancel} className={`${detailTbBtn} ${detailTbDanger}`}>
                    <RotateCcw className="gg-toolbar-ic gg-toolbar-ic--red shrink-0" aria-hidden />
                    <span>[Cancel]</span>
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* Object — name, description, type (matches candidate Parameters) */}
            <section>
              <h3 className="ggj-profile-section-h">Object</h3>
              <div className="ggj-profile-section-body">
                {paramsEditing ? (
                  <div className="ggj-attr-grid">
                    <dt className="vscode-text-secondary pr-2 min-w-0">Name</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">
                      <input className="input w-full max-w-sm" value={paramsForm.name} onChange={(e) => setParamsForm((f) => ({ ...f, name: e.target.value }))} />
                    </dd>
                    <dt className="vscode-text-secondary pr-2 min-w-0">Object type</dt>
                    <dd className="vscode-text-secondary min-w-0 break-words text-sm">Client (read-only)</dd>
                    <dt className="vscode-text-secondary pr-2 min-w-0">Description</dt>
                    <dd className="vscode-text-primary min-w-0 break-words lg:col-span-3">
                      <textarea className="input w-full max-w-sm max-h-28 resize-y overflow-y-auto" rows={3} value={paramsForm.description} onChange={(e) => setParamsForm((f) => ({ ...f, description: e.target.value }))} />
                    </dd>
                  </div>
                ) : (
                  <dl className="ggj-attr-grid">
                    <dt className="vscode-text-secondary pr-2 min-w-0">Name</dt>
                    <dd className="vscode-text-primary min-w-0 break-words font-medium">{client.name || '—'}</dd>
                    <dt className="vscode-text-secondary pr-2 min-w-0">Object type</dt>
                    <dd className="vscode-text-secondary min-w-0 break-words text-sm">Client (read-only)</dd>
                    <dt className="vscode-text-secondary pr-2 min-w-0">Description</dt>
                    <dd className="vscode-text-primary min-w-0 break-words lg:col-span-3">
                      <textarea
                        readOnly
                        tabIndex={-1}
                        rows={4}
                        value={client.notes?.trim() ? client.notes : ''}
                        placeholder="—"
                        aria-label="Description"
                        className="ggj-param-readonly-textarea"
                      />
                    </dd>
                  </dl>
                )}
              </div>
            </section>

            {/* Company & Contact */}
            <section>
              <h3 className="ggj-profile-section-h">[Company &amp; Contact]</h3>
              <div className="ggj-profile-section-body">
                {paramsEditing ? (
                  <div className="ggj-attr-grid">
                    <dt className="vscode-text-secondary pr-2 min-w-0">Domain</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">
                      <input className="input w-full max-w-sm" value={paramsForm.domain} onChange={(e) => setParamsForm((f) => ({ ...f, domain: e.target.value }))} />
                    </dd>
                    <dt className="vscode-text-secondary pr-2 min-w-0">Industry</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">
                      <input className="input w-full max-w-sm" value={paramsForm.industry} onChange={(e) => setParamsForm((f) => ({ ...f, industry: e.target.value }))} />
                    </dd>
                    <dt className="vscode-text-secondary pr-2 min-w-0">Contact Name</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">
                      <input className="input w-full max-w-sm" value={paramsForm.contact_name} onChange={(e) => setParamsForm((f) => ({ ...f, contact_name: e.target.value }))} />
                    </dd>
                    <dt className="vscode-text-secondary pr-2 min-w-0">Contact Email</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">
                      <input className="input w-full max-w-sm" type="email" value={paramsForm.contact_email} onChange={(e) => setParamsForm((f) => ({ ...f, contact_email: e.target.value }))} />
                    </dd>
                    <dt className="vscode-text-secondary pr-2 min-w-0">Contact Phone</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">
                      <input className="input w-full max-w-sm" value={paramsForm.contact_phone} onChange={(e) => setParamsForm((f) => ({ ...f, contact_phone: e.target.value }))} />
                    </dd>
                  </div>
                ) : (
                  <dl className="ggj-attr-grid">
                    <dt className="vscode-text-secondary pr-2 min-w-0">Domain</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">{client.domain || '—'}</dd>
                    <dt className="vscode-text-secondary pr-2 min-w-0">Industry</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">{client.industry || '—'}</dd>
                    <dt className="vscode-text-secondary pr-2 min-w-0">Contact Name</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">{client.contact_name || '—'}</dd>
                    <dt className="vscode-text-secondary pr-2 min-w-0">Contact Email</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">{client.contact_email || '—'}</dd>
                    <dt className="vscode-text-secondary pr-2 min-w-0">Contact Phone</dt>
                    <dd className="vscode-text-primary min-w-0 break-words">{client.contact_phone || '—'}</dd>
                  </dl>
                )}
              </div>
            </section>

            {/* Modification info (always read-only) */}
            <section>
              <h3 className="ggj-profile-section-h">[System Attributes]</h3>
              <div className="ggj-profile-section-body">
                <dl className="ggj-attr-grid">
                  <dt className="vscode-text-secondary pr-2 min-w-0">Created When</dt>
                  <dd className="vscode-text-tertiary min-w-0 break-words text-xs">
                    {client.created_at ? formatDateTimeGmt(client.created_at) : '—'}
                  </dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Created By</dt>
                  <dd className="vscode-text-tertiary min-w-0 break-words text-xs">—</dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Modified When</dt>
                  <dd className="vscode-text-tertiary min-w-0 break-words text-xs">—</dd>
                  <dt className="vscode-text-secondary pr-2 min-w-0">Modified By</dt>
                  <dd className="vscode-text-tertiary min-w-0 break-words text-xs">—</dd>
                </dl>
              </div>
            </section>
          </div>
        </div>
      )}

          </>
        }
      />

      {recipientPopover && (
        <RecipientInfoPopover
          thread={recipientPopover.thread}
          clientName={(recipientPopover.thread.application as { client?: { name: string } } | undefined)?.client?.name}
          onClose={() => setRecipientPopover(null)}
          anchor={recipientPopover.anchor}
        />
      )}

      <ConfirmDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        title="Remove Client"
        subtitle="Jobs and applications may be affected"
        message={`Remove ${client.name}? This will affect associated jobs and applications.`}
        confirmLabel="Remove"
        requireTypedConfirm={client.name}
      />
    </div>
  );
}
